import { gql } from '@urql/core';
import { useAppState } from 'client/providers/AppProvider';
import React, { useCallback, useMemo } from 'react';
import {
    CollegeProfileInfoFragment,
    Espin_Follow_Insert_Input,
    Espin_Role_Enum,
    ParentFollowsMutation,
    ParentFollowsMutationVariables,
    ParentFollowsDocument,
    ParentUnfollowsMutation,
    ParentUnfollowsMutationVariables,
    ParentUnfollowsDocument,
    FollowMutation,
    FollowMutationVariables,
    FollowDocument,
    Espin_Follow_Type_Enum,
    UnfollowMutation,
    UnfollowMutationVariables,
    UnfollowDocument,
    CheckFollowingStatusQuery,
    CheckFollowingStatusQueryVariables,
    CheckFollowingStatusDocument,
} from '../../../generated/spin-graphql';
import { PlainTextLocationDisplay } from '../../common/PlainTextLocationDisplay';
import { PictureWithFallback } from '../../sections/PictureWithFallback';
import { CollegeSocialsDisplay } from '../CollegeSocialsDisplay';
import { CollegeInfoButtons } from './CollegeInfoButtons';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { MessageIcon } from '../../svg/MessageIcon';

type Props = {
    college: CollegeProfileInfoFragment;
};
gql`
    mutation ParentFollows($input: [espin_follow_insert_input!]!) {
        insert_espin_follow(objects: $input) {
            returning {
                id
            }
        }
    }
    mutation ParentUnfollows($playerUserId: [String!]!, $collegeId: uuid!) {
        delete_espin_follow(where: { player_user_id: { _in: $playerUserId }, college_id: { _eq: $collegeId } }) {
            returning {
                id
            }
        }
    }
`;

gql`
    mutation Follow($input: espin_follow_insert_input!) {
        insert_espin_follow_one(object: $input) {
            id
        }
    }
`;

gql`
    mutation Unfollow($where: espin_follow_bool_exp!) {
        delete_espin_follow(where: $where) {
            returning {
                id
            }
        }
    }
`;

gql`
    query CheckFollowingStatus($where: espin_follow_bool_exp!) {
        espin_follow(where: $where) {
            id
        }
    }
`;

gql`
    fragment CollegeProfileInfo on espin_college {
        id
        program_name
        in_state_tuition
        phone
        logo {
            ...FormFile
        }
        contact_email
        out_of_state_tuition
        address {
            ...PlainTextLocationDisplay
        }
        socials {
            facebook
            twitch
            twitter
            instagram
            discord
            youtube_channel {
                id
            }
        }
        ...CollegeSocials
        ...CollegeInfoButtons
    }
`;

export const CollegeProfileInfo: React.FC<Props> = ({ college }) => {
    const { user, subscribed } = useAppState();
    const followType =
        user?.role === Espin_Role_Enum.EspinHighschoolUser
            ? Espin_Follow_Type_Enum.HighSchoolFollowsCollege
            : Espin_Follow_Type_Enum.PlayerFollowsCollege;
    const router = useRouter();
    const [_, follow] = useAuthenticatedMutation<FollowMutation, FollowMutationVariables>(FollowDocument);
    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);

    const [____, followParent] = useAuthenticatedMutation<ParentFollowsMutation, ParentFollowsMutationVariables>(
        ParentFollowsDocument,
    );

    const [_____, unFollowParent] = useAuthenticatedMutation<ParentUnfollowsMutation, ParentUnfollowsMutationVariables>(
        ParentUnfollowsDocument,
    );

    const subscribedChildID: string = useMemo(() => {
        let subbedId = '';

        if (user?.role === Espin_Role_Enum.EspinParentUser) {
            user?.parent_profile?.child_profiles?.forEach((player) => {
                // CEH-600 - Rollback
                if (player?.user.subscription_status.is_subscribed) {
                    subbedId = player?.user_id;
                }
            });
        }
        return subbedId;
    }, [user?.parent_profile?.child_profiles, user?.role]);

    const { isReady, data: followingStatus } = useAuthenticatedQuery<
        CheckFollowingStatusQuery,
        CheckFollowingStatusQueryVariables
    >(
        {
            query: CheckFollowingStatusDocument,
            variables: useMemo(
                () => ({
                    where: {
                        college_id: { _eq: college?.id },
                        ...(followType === Espin_Follow_Type_Enum.PlayerFollowsCollege && {
                            player_user_id:
                                user?.role === Espin_Role_Enum.EspinParentUser
                                    ? { _eq: subscribedChildID }
                                    : { _eq: user?.id },
                        }),
                        ...(followType === Espin_Follow_Type_Enum.HighSchoolFollowsCollege && {
                            high_school_id: { _eq: user?.high_school_profile?.high_school?.id },
                        }),
                        follow_type: { _eq: followType },
                    },
                }),
                [
                    college?.id,
                    user?.high_school_profile?.high_school?.id,
                    followType,
                    subscribedChildID,
                    user?.id,
                    user?.role,
                ],
            ),
            pause: !user,
        },
        ['espin_follow'],
    );
    const followCollege = useCallback(
        (role: Espin_Role_Enum) => {
            switch (role) {
                case Espin_Role_Enum.EspinPlayerUser:
                    follow(
                        {
                            input: {
                                college_id: college?.id,
                            },
                        },
                        { additionalTypenames: ['espin_follow'] },
                    );

                    break;
                case Espin_Role_Enum.EspinHighschoolUser:
                    follow(
                        {
                            input: {
                                college_id: college?.id,
                                high_school_id: user?.high_school_profile?.high_school?.id,
                            },
                        },
                        { additionalTypenames: ['espin_follow'] },
                    );

                    break;
                case Espin_Role_Enum.EspinParentUser:
                    const obs: Espin_Follow_Insert_Input[] = [];
                    // CEH-600 Player Sub hotfix
                    user?.parent_profile?.child_profiles?.forEach((player) => {
                        if (player?.user.subscription_status.is_subscribed) {
                            obs.push({
                                player_user_id: player.user_id,
                                college_id: college?.id,
                            });
                        }
                    });
                    followParent({ input: obs }, { additionalTypenames: ['espin_follow'] });
                    break;
            }
        },
        [
            college?.id,
            follow,
            followParent,
            user?.parent_profile?.child_profiles,
            user?.high_school_profile?.high_school?.id,
        ],
    );

    const unfollowCollege = useCallback(
        (role: Espin_Role_Enum) => {
            const collegeId = college?.id;
            switch (role) {
                case Espin_Role_Enum.EspinPlayerUser:
                    unFollow(
                        {
                            where: {
                                college_id: { _eq: collegeId },
                                follow_type: { _eq: followType },
                            },
                        },
                        { additionalTypenames: ['espin_follow'] },
                    );
                    break;
                case Espin_Role_Enum.EspinHighschoolUser:
                    unFollow(
                        {
                            where: {
                                college_id: { _eq: collegeId },
                                high_school_id: { _eq: user?.high_school_profile?.high_school?.id },
                                follow_type: { _eq: followType },
                            },
                        },
                        { additionalTypenames: ['espin_follow'] },
                    );
                    break;
                case Espin_Role_Enum.EspinParentUser:
                    //we unfollow all subbed players
                    const playerIds: string[] = [];
                    user?.parent_profile?.child_profiles?.forEach((player) => {
                        // CEH-600 Player sub hotfix
                        if (player?.user.subscription_status.is_subscribed) {
                            playerIds.push(player.user_id);
                        }
                    });
                    unFollowParent({
                        playerUserId: playerIds,
                        collegeId,
                    });
                    break;
            }
        },
        [
            college,
            followType,
            unFollow,
            unFollowParent,
            user?.high_school_profile?.high_school?.id,
            user?.parent_profile?.child_profiles,
        ],
    );

    const canSeeFollowButton = useMemo(() => user?.role !== Espin_Role_Enum.EspinCollegeUser, [user?.role]);

    const handleButton = useCallback(() => {
        if (
            !user ||
            (!subscribed && subscribedChildID === '' && followType !== Espin_Follow_Type_Enum.HighSchoolFollowsCollege)
        )
            return router.push('/pricing');
        if (followingStatus?.espin_follow.length <= 0) {
            followCollege(user?.role);
        } else {
            unfollowCollege(user?.role);
        }
    }, [
        followType,
        followCollege,
        followingStatus?.espin_follow.length,
        router,
        subscribed,
        unfollowCollege,
        user,
        subscribedChildID,
    ]);

    const formatter = useMemo(
        () =>
            Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }),
        [],
    );

    const inState: string =
        college.in_state_tuition && college.in_state_tuition !== 'N/A'
            ? formatter.format(parseInt(college.in_state_tuition.replace(/\$|,/g, '')))
            : 'N/A';

    const outOfState: string =
        college.out_of_state_tuition && college.out_of_state_tuition !== 'N/A'
            ? formatter.format(parseInt(college.out_of_state_tuition.replace(/\$|,/g, '')))
            : 'N/A';

    const hasPlayerInfoButtons = college.phone || college.contact_email;
    const hasSocials =
        college.socials?.facebook ||
        college.socials?.twitter ||
        college.socials?.instagram ||
        college.socials?.discord ||
        college.socials?.youtube_channel;

    const isPlayerOrParentOrHighSchool =
        user?.role === Espin_Role_Enum.EspinPlayerUser ||
        user?.role === Espin_Role_Enum.EspinParentUser ||
        user?.role === Espin_Role_Enum.EspinHighschoolUser;

    return (
        <div className="college-profile-info">
            <div className="left-panel">
                <div className="college-logo-info-container">
                    <div className="college-profile-image">
                        <PictureWithFallback
                            icon={college.logo}
                            width={100}
                            height={100}
                            alt={`${college.program_name}} profile picture`}
                        />
                    </div>
                    <div className="bio">
                        <h1 className="college-name">{college.program_name}</h1>
                        <p className="college-location">
                            <PlainTextLocationDisplay address={college.address} />
                        </p>
                        {hasSocials && <CollegeSocialsDisplay socials={college.socials} />}
                    </div>
                </div>
                {hasPlayerInfoButtons && <CollegeInfoButtons college={college} />}
            </div>
            <div className="right-panel">
                {(isPlayerOrParentOrHighSchool || canSeeFollowButton) && (
                    <div className="social-controls-container">
                        {isPlayerOrParentOrHighSchool && (
                            <Link href={subscribed ? `/dashboard/messages/?college_slug=${college.slug}` : `/pricing`}>
                                <a>
                                    <button className="button circle-button" title="Send Message">
                                        <MessageIcon />
                                    </button>
                                </a>
                            </Link>
                        )}
                        {isReady && canSeeFollowButton && (
                            <button
                                onClick={handleButton}
                                className={`button medium-button${
                                    followingStatus?.espin_follow.length > 0 ? ' red-button' : ' blue-button'
                                }`}
                            >
                                {followType === Espin_Follow_Type_Enum.HighSchoolFollowsCollege ||
                                subscribed ||
                                subscribedChildID != ''
                                    ? followingStatus?.espin_follow.length <= 0
                                        ? 'Follow'
                                        : 'Unfollow'
                                    : 'Upgrade'}
                            </button>
                        )}
                    </div>
                )}
                {(!!inState || !!outOfState) && (
                    <div className="tuition-container">
                        {!!inState && (
                            <div className="tuition-card">
                                <p className="title">In State Tuition</p>
                                <p className="value">{inState}</p>
                            </div>
                        )}
                        {!!outOfState && (
                            <div className="tuition-card">
                                <p className="title">Out of State Tuition</p>
                                <p className="value">{outOfState}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
